import api from './api';
import { CreateClientPayload, Client, ClientOnboardingFormData, UpdateClientData, ClientSpaceInfo } from '../types';

export const clientService = {
  // Create client with full onboarding (uses backend transaction)
  createClientWithOnboarding: async (formData: ClientOnboardingFormData): Promise<Client> => {
    const payload = {
      companyName: formData.companyName,
      registrationNumber: formData.registrationNumber || undefined,
      gstNumber: formData.gstNumber || undefined,
      panNumber: formData.panNumber || undefined,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      pinCode: formData.pinCode,
      country: formData.country,
      contactPersonName: formData.contactPersonName,
      designation: formData.designation || undefined,
      email: formData.email,
      phone: formData.phone,
      alternatePhone: formData.alternatePhone || undefined,
      clientType: formData.clientType,
      // 'both' clients send both limits (pallets for racks, sqft for zones)
      palletCapacity:
        formData.clientType === 'per_pallet' || formData.clientType === 'both'
          ? formData.palletCapacity
          : undefined,
      sqftCapacity:
        formData.clientType === 'per_sqft' || formData.clientType === 'both'
          ? formData.sqftCapacity
          : undefined,
      status: 'active',
      productCategoryIds: formData.productCategoryIds,
      productTypeIds: formData.productTypeIds,
      storageRequirementIds: formData.storageRequirementIds, // Changed to array
      handlingInstructions: formData.handlingInstructions || undefined,
      warehouseId: formData.warehouseId,
    };

    const response = await api.post('/clients', payload);
    const responseData = response as any;
    return responseData.data;
  },

  // Create basic client (original endpoint)
  createClient: async (clientData: CreateClientPayload): Promise<Client> => {
    const response = await api.post('/clients', clientData);
    const responseData = response as any;
    return responseData.data;
  },

  // Get client by ID
  getClient: async (id: number): Promise<Client> => {
    const response = await api.get(`/clients/${id}`);
    
    const responseData = response as any;
    
    if (!responseData || !responseData.data) {
      throw new Error('Client not found or invalid response structure');
    }
    
    return responseData.data;
  },

  // Update client
  updateClient: async (id: number, updateData: UpdateClientData): Promise<Client> => {
    const payload = {
      companyName: updateData.companyName,
      registrationNumber: updateData.registrationNumber || undefined,
      gstNumber: updateData.gstNumber || undefined,
      panNumber: updateData.panNumber || undefined,
      address: updateData.address,
      city: updateData.city,
      state: updateData.state,
      pinCode: updateData.pinCode,
      country: updateData.country,
      contactPersonName: updateData.contactPersonName,
      designation: updateData.designation || undefined,
      email: updateData.email,
      phone: updateData.phone, // Use 'phone' field name
      alternatePhone: updateData.alternatePhone || undefined,
      clientType: updateData.clientType,
      // 'both' clients send both limits (pallets for racks, sqft for zones)
      palletCapacity:
        updateData.clientType === 'per_pallet' || updateData.clientType === 'both'
          ? updateData.palletCapacity
          : undefined,
      sqftAssigned:
        updateData.clientType === 'per_sqft' || updateData.clientType === 'both'
          ? updateData.sqftAssigned
          : undefined, // Use 'sqftAssigned' field name
      additionalSpaceSqft: updateData.additionalSpaceSqft,
      status: updateData.status,
      productCategoryIds: updateData.productCategoryIds,
      productTypeIds: updateData.productTypeIds,
      storageRequirementIds: updateData.storageRequirementIds, // Changed to array
      handlingInstructions: updateData.handlingInstructions || undefined,
      warehouseId: updateData.warehouseId,
    };

    const response = await api.patch(`/clients/${id}`, payload);
    const responseData = response as any;
    return responseData.data;
  },

  // Export clients to CSV
  exportClientsCsv: async (filters?: { search?: string; sort?: string; order?: string }): Promise<void> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.sort) params.append('sort', filters.sort);
    if (filters?.order) params.append('order', filters.order);

    const queryString = params.toString();
    const url = `/clients/export/csv${queryString ? `?${queryString}` : ''}`;

    // Use api.get with responseType blob (same pattern as reports)
    const response = await api.get(url, {
      responseType: 'blob',
    }) as unknown as Blob;

    // Create blob and download
    const blob = new Blob([response], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `clients-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  // Get client space information
  getClientSpaceInfo: async (clientId: number): Promise<ClientSpaceInfo> => {
    const response = await api.get(`/clients/${clientId}/space-info`);
    const responseData = response as any;
    return responseData.data;
  },

  // Get warehouse utilization
  getWarehouseUtilization: async (): Promise<import('../types').WarehouseUtilization> => {
    const response = await api.get('/clients/warehouse/utilization');
    const responseData = response as any;
    return responseData.data;
  },
};
